/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { Tag } from '@coursera/cds-core';
import { FlagFilledIcon, FlagIcon } from '@coursera/cds-icons';

import { PlagiarismContext } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/PlagiarismContext';
import { getLabelForRuling } from 'bundles/author-assignment-grading/utils/plagiarismFlagUtils';
import LoadingSkeleton from 'bundles/grading/components/common/LoadingSkeleton';
import PlagiarismDrawer from 'bundles/grading/components/staff-graded-assignments/plagiarism/PlagiarismDrawer';
import { CHEATING_CASE_RULINGS } from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';

import _t from 'i18n!nls/grading';

const styles = {
  flagContainer: css`
    margin: var(--cds-spacing-100) 0 0 0;

    &:hover {
      cursor: pointer;
    }
  `,
};

type ValueOf<T> = T[keyof T];

const getColorForRuling = (ruling: ValueOf<typeof CHEATING_CASE_RULINGS>) => {
  switch (ruling) {
    case CHEATING_CASE_RULINGS.RESOLVED_NO_CHEATING:
      return 'success';
    case CHEATING_CASE_RULINGS.RESOLVED_CHEATING:
      return 'warning';
    case CHEATING_CASE_RULINGS.UNRESOLVED:
    default:
      return 'error';
  }
};

const PlagiarismFlag = () => {
  const { cheatingCaseStaffComments, ruling, fetching } = useContext(PlagiarismContext);
  const hasFlag = Boolean(cheatingCaseStaffComments && cheatingCaseStaffComments.length);
  const [isPlagiarismDrawerOpen, setPlagiarismDrawerOpen] = useState(false);

  return (
    <div css={styles.flagContainer}>
      <PlagiarismDrawer isOpen={isPlagiarismDrawerOpen} onClose={() => setPlagiarismDrawerOpen(false)} />
      {fetching && <LoadingSkeleton width="7vw" height="25.5px" />}
      {!fetching && hasFlag && (
        <Tag
          priority="secondary"
          variant="status"
          color={getColorForRuling(ruling)}
          icon={<FlagFilledIcon />}
          role="button"
          onClick={() => setPlagiarismDrawerOpen(true)}
        >
          {getLabelForRuling(ruling)}
        </Tag>
      )}
      {!fetching && !hasFlag && (
        <Tag priority="secondary" variant="status" icon={<FlagIcon />} onClick={() => setPlagiarismDrawerOpen(true)}>
          {_t('No plagiarism flag')}
        </Tag>
      )}
    </div>
  );
};

export default PlagiarismFlag;
